// Conflict resolution for PouchDB replication.
//
// Two devices editing the same doc offline produce conflicting revisions after
// they sync. PouchDB keeps a deterministic but arbitrary winner and leaves the
// losing branches around, so the UI silently shows one edit and the other is
// lost-but-not-gone. This module converges every conflicted doc on a single
// value with a last-write-wins policy (by the `updatedAt` stamp the adapter
// writes), then deletes the losing branches so the store reads one clean value.

import type { BaseDoc } from '@/lib/store-adapter';

interface Versioned {
  _id: string;
  _rev: string;
  updatedAt?: number;
}

// Compare two _rev strings ("<gen>-<hash>"). Returns true if `a` should win over
// `b` by CouchDB's own deterministic rule — higher generation number wins, ties
// broken by the lexicographically larger hash — so every replica converges on
// the same winner when timestamps are equal.
export function revWins(a: string, b: string): boolean {
  const [ag, ah] = splitRev(a);
  const [bg, bh] = splitRev(b);
  if (ag !== bg) return ag > bg;
  return ah > bh;
}

function splitRev(rev: string): [number, string] {
  const dash = rev.indexOf('-');
  if (dash === -1) return [0, rev];
  return [parseInt(rev.slice(0, dash), 10) || 0, rev.slice(dash + 1)];
}

// Pure winner selection: newest `updatedAt` wins; ties fall back to revWins so
// the choice is deterministic across devices.
export function pickConflictWinner<T extends Versioned>(versions: T[]): T {
  return versions.reduce((best, v) => {
    const bu = best.updatedAt ?? 0;
    const vu = v.updatedAt ?? 0;
    if (vu !== bu) return vu > bu ? v : best;
    return revWins(v._rev, best._rev) ? v : best;
  });
}

type ConflictDoc = BaseDoc & { _conflicts?: string[]; updatedAt?: number };

// Resolve every conflicted doc in the DB: pick the winner, delete the losing
// leaf revisions. Deleting all leaves except the winner promotes the winner to
// the sole live revision. Returns the number of docs resolved.
export async function resolveConflicts(db: PouchDB.Database): Promise<number> {
  const res = await db.allDocs<ConflictDoc>({
    include_docs: true,
    conflicts: true,
  });
  let resolved = 0;
  for (const row of res.rows) {
    const doc = row.doc;
    const conflicts = doc?._conflicts;
    if (!doc || !conflicts || conflicts.length === 0) continue;

    const revs = [doc._rev as string, ...conflicts];
    const versions: ConflictDoc[] = [];
    for (const rev of revs) {
      try {
        versions.push(await db.get<ConflictDoc>(doc._id, { rev }));
      } catch {
        // a deleted/compacted leaf — ignore it
      }
    }
    if (versions.length < 2) continue;

    const winner = pickConflictWinner(versions as Required<Versioned>[]);
    for (const v of versions) {
      if (v._rev !== winner._rev) {
        await db.remove(v._id as string, v._rev as string);
      }
    }
    resolved++;
  }
  return resolved;
}
